import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import {
  MAX_MINUTES,
  MINUTE_STOPS,
  describeSpeedup,
  formatMinutes,
  nearestStopIndex,
  parseTypedMinutes,
} from './taskTiming';

interface TaskTimingPanelProps {
  /**
   * Persist one estimate for the reply this panel hangs off. Resolution
   * closes the panel; rejection leaves the sliders where they were so the
   * operator can retry.
   */
  onLog: (
    minutesByHand: number,
    minutesWithAgent: number,
    taskName: string | null,
  ) => Promise<void>;
  onCancel: () => void;
  suggestedMinutes?: number;
  /**
   * Keeps the button testids unique when several replies are on screen;
   * the panel itself only opens on one reply at a time.
   */
  index: number;
}

const DEFAULT_MINUTES_WITH_AGENT = 10;

/**
 * Matches the cap on `AgentTaskTimingCreateInput.taskName`, so a long
 * description is trimmed as it is typed rather than rejected by the
 * create hook once the operator taps Log.
 */
const TASK_NAME_MAX_CHARS = 200;

type Estimate = 'byHand' | 'withAgent';

export const VERDICT_COLOR = {
  faster: 'var(--green)',
  same: 'var(--label-secondary)',
  slower: 'var(--red)',
} as const;

const PILL_BUTTON_CLASS = cx(
  'tw-py-6',
  'tw-px-12',
  'tw-rounded-full',
  'tw-border-none',
  'tw-cursor-pointer',
);

/**
 * Inline card under a reply's thumbs row: two log-scale sliders, by hand
 * versus with the agent, a live "×2.3 faster · saved 12m" readout, and one
 * Log. Each slider's value is also a button that opens a number field, for
 * an exact figure the stops cannot express; the slider then sits on the
 * nearest stop. A pure estimate, asked while the task is fresh; the row it
 * writes hangs off the reply that finished the task.
 */
export function TaskTimingPanel({
  onLog,
  onCancel,
  suggestedMinutes,
  index,
}: TaskTimingPanelProps) {
  const [minutesByHand, setMinutesByHand] = useState(
    suggestedMinutes ?? DEFAULT_MINUTES_WITH_AGENT,
  );

  const [minutesWithAgent, setMinutesWithAgent] = useState(
    suggestedMinutes ?? DEFAULT_MINUTES_WITH_AGENT,
  );

  const editedAgentMinutes = useRef(false);
  const editedHandMinutes = useRef(false);
  const [editing, setEditing] = useState<Estimate | null>(null);
  const [draft, setDraft] = useState('');
  const editingRef = useRef(editing);

  editingRef.current = editing;

  useEffect(() => {
    if (suggestedMinutes === undefined) return;

    if (!editedAgentMinutes.current) {
      setMinutesWithAgent(suggestedMinutes);
      if (editingRef.current === 'withAgent')
        setDraft(String(suggestedMinutes));
    }

    if (!editedHandMinutes.current) {
      setMinutesByHand(suggestedMinutes);
      if (editingRef.current === 'byHand') setDraft(String(suggestedMinutes));
    }
  }, [suggestedMinutes]);

  const [isLogging, setIsLogging] = useState(false);
  const [taskName, setTaskName] = useState('');

  const summary = describeSpeedup(minutesByHand, minutesWithAgent);

  const handleLog = () => {
    if (isLogging) return;

    editedAgentMinutes.current = true;
    editedHandMinutes.current = true;
    setIsLogging(true);

    const trimmedName = taskName.trim();

    onLog(minutesByHand, minutesWithAgent, trimmedName || null)
      .catch(() => {
        // The caller logs the failure; keep the sliders so a retry is one
        // tap. The parent closes the panel on success.
      })
      .finally(() => setIsLogging(false));
  };

  const commitDraft = (setMinutes: (next: number) => void) => {
    const typed = parseTypedMinutes(draft);

    if (typed !== null) setMinutes(typed);
    setEditing(null);
  };

  const renderSlider = (
    label: string,
    estimate: Estimate,
    minutes: number,
    setMinutes: (next: number) => void,
    testId: string,
  ) => {
    const isEditing = editing === estimate;

    return (
      <div
        className="tw-flex tw-items-center tw-gap-8"
        style={{ fontSize: 11, color: 'var(--label-tertiary)' }}
      >
        <span style={{ width: 62, flexShrink: 0 }}>{label}</span>
        <input
          type="range"
          min={0}
          max={MINUTE_STOPS.length - 1}
          step={1}
          value={nearestStopIndex(minutes)}
          onChange={(event) => {
            if (estimate === 'withAgent') editedAgentMinutes.current = true;
            else editedHandMinutes.current = true;
            setMinutes(MINUTE_STOPS[Number(event.target.value)]);
          }}
          aria-label={label}
          aria-valuetext={formatMinutes(minutes)}
          data-testid={testId}
          className="tw-flex-1 tw-min-w-0"
          style={{ accentColor: 'var(--orange)' }}
        />
        {isEditing ? (
          <input
            type="number"
            inputMode="numeric"
            min={1}
            max={MAX_MINUTES}
            step={1}
            value={draft}
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
            onFocus={(event) => event.target.select()}
            onChange={(event) => {
              if (estimate === 'withAgent') editedAgentMinutes.current = true;
              else editedHandMinutes.current = true;
              setDraft(event.target.value);
            }}
            onBlur={() => commitDraft(setMinutes)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                commitDraft(setMinutes);
              } else if (event.key === 'Escape') {
                event.stopPropagation();
                setEditing(null);
              }
            }}
            aria-label={`${label}, minutes`}
            data-testid={`${testId.replace(/-slider$/, '')}-value-input`}
            className="tw-text-right tw-font-medium tw-rounded-6 tw-outline-none"
            style={{
              width: 44,
              flexShrink: 0,
              boxSizing: 'border-box',
              padding: '2px 4px',
              fontSize: 11,
              fontFamily: 'inherit',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--label-primary)',
              background: 'var(--surface-primary-elevated)',
              border: '1px solid var(--orange)',
            }}
          />
        ) : (
          <button
            type="button"
            title="Type an exact number of minutes"
            aria-label={`${label}: ${formatMinutes(minutes)}. Type an exact number of minutes`}
            data-testid={`${testId.replace(/-slider$/, '')}-value-button`}
            onClick={() => {
              setDraft(String(minutes));
              setEditing(estimate);
            }}
            className="tw-text-right tw-font-medium tw-border-none tw-bg-transparent tw-p-0 tw-cursor-text"
            style={{
              width: 44,
              flexShrink: 0,
              fontSize: 11,
              fontFamily: 'inherit',
              fontVariantNumeric: 'tabular-nums',
              color: 'var(--label-primary)',
              textDecoration: 'underline dotted var(--label-quaternary)',
              textUnderlineOffset: 3,
            }}
          >
            {formatMinutes(minutes)}
          </button>
        )}
      </div>
    );
  };

  return (
    <div
      data-testid="chat-timing-panel"
      role="group"
      aria-label="Task timing"
      className={cx(
        'tw-flex',
        'tw-flex-col',
        'tw-gap-8',
        'tw-rounded-12',
        'tw-px-12',
        'tw-py-10',
      )}
      style={{
        // Card fill uses the themeable token; the hairline keeps it
        // readable against the reply text on both themes.
        background: 'var(--fill-primary)',
        border: '1px solid var(--divider-primary)',
      }}
    >
      <div
        style={{
          fontSize: 12,
          fontWeight: 600,
          color: 'var(--label-primary)',
        }}
      >
        How long did this task take?
      </div>
      <input
        value={taskName}
        onChange={(event) => setTaskName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleLog();
          }
        }}
        maxLength={TASK_NAME_MAX_CHARS}
        placeholder="What was the task? e.g. pick & place from a paragraph"
        aria-label="Task"
        data-testid={`chat-timing-task-field-${index}`}
        className="tw-w-full tw-rounded-8 tw-border-none tw-outline-none"
        style={{
          boxSizing: 'border-box',
          padding: '6px 10px',
          fontSize: 12,
          fontFamily: 'inherit',
          color: 'var(--label-primary)',
          background: 'var(--surface-primary-elevated)',
          border: '1px solid var(--divider-primary)',
        }}
      />
      <span
        style={{ fontSize: 11, color: 'var(--label-tertiary)' }}
        data-testid="chat-timing-estimate-hint"
      >
        With-agent time is suggested from the conversation. Adjust for breaks or
        other work before logging.
      </span>
      {renderSlider(
        'By hand',
        'byHand',
        minutesByHand,
        setMinutesByHand,
        'chat-timing-by-hand-slider',
      )}
      {renderSlider(
        'With agent',
        'withAgent',
        minutesWithAgent,
        setMinutesWithAgent,
        'chat-timing-with-agent-slider',
      )}
      <div className="tw-flex tw-items-center tw-gap-8">
        <span
          data-testid="chat-timing-readout"
          style={{
            fontSize: 13,
            fontWeight: 600,
            color: VERDICT_COLOR[summary.verdict],
          }}
        >
          {summary.label}
        </span>
        <span className="tw-flex-1" />
        <button
          type="button"
          data-testid={`chat-timing-cancel-button-${index}`}
          onClick={onCancel}
          className={cx(PILL_BUTTON_CLASS, 'tw-bg-transparent')}
          style={{
            fontSize: 12,
            fontWeight: 500,
            fontFamily: 'inherit',
            color: 'var(--label-secondary)',
          }}
        >
          Cancel
        </button>
        <button
          type="button"
          data-testid={`chat-timing-log-button-${index}`}
          onClick={handleLog}
          disabled={isLogging}
          className={cx(
            PILL_BUTTON_CLASS,
            'tw-text-light',
            'tw-whitespace-nowrap',
          )}
          style={{
            // 12/600 isn't in the typography scale.
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'inherit',
            background: 'var(--orange)',
            opacity: isLogging ? 0.6 : 1,
          }}
        >
          Log
        </button>
      </div>
    </div>
  );
}
